require('./stylesheets/main.scss');

import React from 'react';
import ReactDOM from 'react-dom';

import VideoContainer from './VideoContainer.react';
import PlayerDashboard from './dashboard/PlayerDashboard.react';
import LiveLabel from './LiveLabel.react';
import LoadingOverlay from './LoadingOverlay.react';
import StartOverlay from './StartOverlay.react';
import DebugOverlay from './DebugOverlay.react';
import CenteredOverlay from './CenteredOverlay.react';
import PlayPause from './PlayPause.react';
import ViewerCount from './ViewerCount.react';
import {
  isBrowserIOS,
  chromeVersion,
  firefoxVersion,
  isHandheld
} from './utils';

class BambuserPlayerReact extends React.Component {
  constructor(props) {
    super(props);
    this.hoverTimer = null;
    this.state = {
      playerState: {
        currentTime: null,
        duration: null,
        isLive: false,
        isWaiting: false,
        hasEnded: false,
        volume: 1,
        paused: true
      },
      canPlay: false,
      isFullscreen: false,
      isHandheld: isHandheld(),
      isActive: false,
      started: false,
      playbackStarted: false
    };

    this.update = this.update.bind(this);
    this.toggleFullscreen = this.toggleFullscreen.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onClickVideo = this.onClickVideo.bind(this);
    this.onProgress = this.onProgress.bind(this);
    this.onVideoCanPlay = this.onVideoCanPlay.bind(this);
    this.onVideoPlaying = this.onVideoPlaying.bind(this);
    this.onVideoPaused = this.onVideoPaused.bind(this);
    this.onVideoWaiting = this.onVideoWaiting.bind(this);
    this.onVideoSeeked = this.onVideoSeeked.bind(this);
    this.setupVideoElement = this.setupVideoElement.bind(this);
    this.startVideo = this.startVideo.bind(this);
    this.onFullscreenChangeEvent = this.onFullscreenChangeEvent.bind(this);
  }

  update() {
    var started = this.state.started;
    if (!started && !this.props.video.paused) {
      started = true;
    }
    this.setState({
      started: started,
      error: this.props.video.error,
      playerState: {
        currentTime: this.props.video.currentTime,
        duration: this.props.video.duration !== Infinity ? this.props.video.duration : null,
        isLive: this.props.video.isLive,
        isWaiting: this.state.playerState.isWaiting && !this.props.video.ended,
        hasEnded: this.props.video.ended,
        volume: this.props.video.volume,
        paused: this.props.video.paused,
        seeking: this.props.video.seeking
      }
    });
  }

  activatePlayerUI() {
    this.setState({isActive: true});
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => {
      this.setState({isActive: false});
    }, 2500);
  }

  deactivatePlayerUI() {
    clearTimeout(this.hoverTimer);
    this.setState({isActive: false});
  }

  onMouseMove() {
    if (this.state.isHandheld) return;
    this.activatePlayerUI();
  }

  onMouseLeave() {
    if (this.state.isHandheld) return;
    this.deactivatePlayerUI();
  }

  onTouchMove() {
    if (this.state.isActive || this.state.playerState.paused) return;
    this.activatePlayerUI();
  }

  onClickVideo(e) {
    e.stopPropagation();
    e.preventDefault();

    if (this.state.isHandheld) {
      // Handle clicks events on handheld devices differently.
      // Require one initial click to grab the attention of the player.
      if (!this.state.isActive && !this.state.playerState.paused) {
        this.activatePlayerUI();
        return;
      }
    }

    if (!this.props.video.isLive || this.props.video.timeshift) {
      // Toggle play/pause
      if (this.props.video.paused) {
        const playPromise = this.props.video.play();
        if (playPromise) playPromise.catch(() => {});
      } else {
        this.props.video.pause();
      }
    }
  }

  onVideoCanPlay() {
    this.setState({canPlay: true});
    this.isWaiting = false;
  }

  onVideoPlaying() {
    this.setState({
      playbackStarted: true
    });
    this.isWaiting = false;
  }

  onVideoPaused() {
    // Enable timeshift mode for timeshift enabled live videos when paused
    if (this.props.timeshift && this.props.video.isLive && !this.props.video.timeshift) {
      this.props.video.timeshift = true;
    }
    this.update();
  }

  onProgress() {
    if (!this.props.video) return;
    if (!this.props.video.buffered) return;
    if (!this.state.playerState.isWaiting) return;
    // Check if currentTime is within buffered range.
    for (let range = 0; range < this.props.video.buffered.length; range++) {
      if (
        this.props.video.currentTime >= this.props.video.buffered.start(range) &&
        this.props.video.currentTime <= this.props.video.buffered.end(range)
      ) {
        this.isWaiting = false;
        break;
      }
    }
  }

  onVideoWaiting() {
    this.isWaiting = true;
  }

  onVideoSeeked() {
    this.isWaiting = false;
  }

  set isWaiting(val) {
    var playerState = this.state.playerState;
    playerState.isWaiting = val;
    this.setState({playerState: playerState});
    this.update();
  }

  startVideo() {
    if (this.props.video) {
      const playPromise = this.props.video.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
        }).catch((e) => { // eslint-disable-line no-unused-vars
          // no-op
        });
      }
    }
  }

  onFullscreenChangeEvent() {
    this.setState({isFullscreen: getFullscreenElement() === this.fullscreenNode});
  }

  get fullscreenNode() {
    let node;
    if (this.props.fullscreenElement) {
      node = this.props.fullscreenElement;
      if (!(node instanceof HTMLElement)) throw new Error('Cannot enter fullscreen with non-HTMLElement');
      if (!this.state.isFullscreen) {
        // Force the element to occupy the full width/height.
        // Save the element's current dimensions and reset them when leaving fullscreen.
        this.fullscreenElementWidth = node.style.width;
        this.fullscreenElementHeight = node.style.height;
        node.style.width = node.style.height = '100%';
      } else {
        node.style.width = this.fullscreenElementWidth;
        node.style.height = this.fullscreenElementHeight;
      }
    } else {
      node = ReactDOM.findDOMNode(this);
    }
    return node;
  }

  toggleFullscreen() {
    var node = this.fullscreenNode;

    // Setup an event listener that keeps the component's isFullscreen state updated
    if (!this._hasAddedFullscreenEventListener) {
      this._hasAddedFullscreenEventListener = true;
      let eventName = 'fullscreenchange';
      if (document.webkitExitFullscreen && (!chromeVersion || chromeVersion < 71)) {
        eventName = 'webkitfullscreenchange';
      }
      if (document.mozCancelFullScreen && (!firefoxVersion || firefoxVersion < 64)) {
        eventName = 'mozfullscreenchange';
      }
      if (document.msExitFullscreen) {
        eventName = 'MSFullscreenChange';
      }
      this._fullscreenEventName = eventName;
      document.addEventListener(eventName, this.onFullscreenChangeEvent);
    }

    var fullscreenElement = getFullscreenElement();

    if (fullscreenElement === node) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      return;
    }

    let fullscreenPromise;
    if (node.requestFullscreen) {
      fullscreenPromise = node.requestFullscreen();
    } else if (node.webkitRequestFullscreen) {
      fullscreenPromise = node.webkitRequestFullscreen();
    } else if (node.mozRequestFullScreen) {
      fullscreenPromise = node.mozRequestFullScreen();
    } else if (node.msRequestFullscreen) {
      fullscreenPromise = node.msRequestFullscreen();
    }
    if (fullscreenPromise) {
      fullscreenPromise.catch((err) => { // eslint-disable-line no-unused-vars
        console.log('Could not enter fullscreen. If using an iframe, ensure the allow="fullscreen" attribute is set.');
      });
    }
  }

  componentDidMount() {
    if (this.props.video) {
      this.setupVideoElement();
      this.update();
      // Use a timeout function to forceupdate the UI to make sure the poster is updated.
      // This is currently needed because of a timeout used to set the video.poster in bambuser-video-js
      this._timeout = setTimeout(() => {
        clearTimeout(this._timeout);
        this.forceUpdate();
      }, 1000);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.video && this.props.video) {
      this.setupVideoElement();
    }
    if (this.state.isFullscreen !== prevState.isFullscreen) {
      if (typeof this.props.onFullscreenChange === 'function') {
        this.props.onFullscreenChange(this.state.isFullscreen);
      }
    }
  }

  setupVideoElement() {

    this.props.video.addEventListener('byteschange', this.update);
    this.props.video.addEventListener('progress', this.onProgress);
    this.props.video.addEventListener('durationchange', this.update);
    this.props.video.addEventListener('ended', this.update);
    this.props.video.addEventListener('error', this.update);
    this.props.video.addEventListener('loadedmetadata', this.update);
    this.props.video.addEventListener('pause', this.onVideoPaused);
    this.props.video.addEventListener('play', this.update);
    this.props.video.addEventListener('seeking', this.update);
    this.props.video.addEventListener('seeked', this.onVideoSeeked);
    this.props.video.addEventListener('timeupdate', this.update);
    this.props.video.addEventListener('volumechange', this.update);
    this.props.video.addEventListener('canplay', this.onVideoCanPlay);
    this.props.video.addEventListener('waiting', this.onVideoWaiting);
    this.props.video.addEventListener('playing', this.onVideoPlaying);

    if (isBrowserIOS) {
      this.props.video.controls = true;
    }
  }

  componentWillUnmount() {
    this.props.video.removeEventListener('byteschange', this.update);
    this.props.video.removeEventListener('progress', this.onProgress);
    this.props.video.removeEventListener('durationchange', this.update);
    this.props.video.removeEventListener('ended', this.update);
    this.props.video.removeEventListener('loadedmetadata', this.update);
    this.props.video.removeEventListener('pause', this.onVideoPaused);
    this.props.video.removeEventListener('play', this.update);
    this.props.video.removeEventListener('seeking', this.update);
    this.props.video.removeEventListener('seeked', this.onVideoSeeked);
    this.props.video.removeEventListener('timeupdate', this.update);
    this.props.video.removeEventListener('volumechange', this.update);
    this.props.video.removeEventListener('canplay', this.onVideoCanPlay);
    this.props.video.removeEventListener('waiting', this.onVideoWaiting);
    this.props.video.removeEventListener('playing', this.onVideoPlaying);

    if (this._hasAddedFullscreenEventListener) {
      document.removeEventListener(this._fullscreenEventName, this.onFullscreenChangeEvent);
    }
  }

  render() {
    var style = {
      width: '100%',
      height: '100%',
      position: 'relative'
    };
    var classNames = 'bambuser-player';
    if (this.state.playerState.paused) {
      classNames += ' paused';
    }
    if (this.state.isActive) {
      classNames += ' hovering';
    }
    if (this.state.isHandheld) {
      classNames += ' handheld';
    }
    if (this.state.started) {
      classNames += ' started';
    }
    if (this.state.playbackStarted) {
      classNames += ' playback-started';
    }

    if (this.state.error) {
      let errorMessage = 'Oops! Something went wrong.';
      if (typeof this.state.error === 'string') {
        errorMessage = this.state.error;
      } else if (this.state.error.message) {
        errorMessage = this.state.error.message;
      }
      return (
        <div className={classNames} style={style}>
          <CenteredOverlay>
            <div className='b-error-message'>{errorMessage}</div>
          </CenteredOverlay>
        </div>
      );
    }

    var liveLabel = null;
    if (this.state.playerState.isLive && !this.state.playerState.hasEnded) {
      liveLabel = <LiveLabel video={this.props.video} />;
    }

    let viewerCount = null;
    if (this.state.playerState.isLive && !this.state.playerState.hasEnded && this.props.showViewerCount) {
      viewerCount = <ViewerCount video={this.props.video} />;
    }

    var debugOverlay = null;
    if (this.props.showDebugOverlay) {
      var logLines = [
        'BambuserPlayerReact build: ' + BUILD,
      ];
      debugOverlay = <DebugOverlay logLines={logLines} />;
    }

    var loadingOverlay = null;
    if (this.state.playerState.seeking || this.state.playerState.isWaiting) {
      loadingOverlay = <LoadingOverlay />;
    }

    var startOverlay = null;
    if (!isBrowserIOS && !this.state.started) {
      startOverlay = <StartOverlay playCallback={this.startVideo} posterUrl={this.props.posterUrl} />;
    }

    var pauseOverlay = null;
    if (
      this.state.started && // rely on startOverlay first
      !isBrowserIOS && // iOS provides their own button
      this.state.isHandheld && // don't show on desktops
      this.state.playerState.paused
    ) {
      pauseOverlay = (
        <CenteredOverlay>
          <PlayPause onClick={this.onClickVideo} onTouchEnd={this.onClickVideo} />
        </CenteredOverlay>
      );
    }

    var dashBoard = null;
    if (!isBrowserIOS && this.state.started) {
      dashBoard = (
        <PlayerDashboard
          video={this.props.video}
          noFullscreen={this.props.noFullscreen}
          timeshift={this.props.timeshift}
          toggleFullscreen={this.toggleFullscreen}
          isFullscreen={this.state.isFullscreen}
          {...this.state.playerState}
          markers={this.props.markers || []} />
      );
    }

    let poster = this.props.posterUrl || this.props.video ? this.props.video.poster : '';
    if (isBrowserIOS) {
      classNames += ' use-native-controls';
      poster = false;
    }

    return (
      <div className={classNames} style={style}
        onMouseMove={this.onMouseMove}
        onMouseLeave={this.onMouseLeave}
        onTouchMove={this.onTouchMove}>
          {debugOverlay}
          {loadingOverlay}
          {pauseOverlay}
          {startOverlay}
          <div className={'b-status-bar ' + (isBrowserIOS ? 'nudge-right' : '')}>
            {liveLabel}
            {viewerCount}
          </div>
          {dashBoard}
          <VideoContainer
            video={this.props.video}
            poster={poster}
            style={style}
            onClick={this.onClickVideo} />
      </div>
    );
  }
}

function getFullscreenElement() {
  return document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement;
}

// This is used by bambuser-video-js' Sentry client
BambuserPlayerReact.BUILD = BUILD;

export default BambuserPlayerReact;
